import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Platform,
  Alert,
} from 'react-native';
import LoadingAnimation from '../components/LoadingAnimation';
import { useToast } from '../contexts/ToastContext';
import Icon from 'react-native-vector-icons/MaterialIcons';
import {
  getEventById,
  deleteEvent,
  getEventCheckIns,
  updateEventCheckInMode,
} from '../services/database';

const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const ManageEventScreen = ({ navigation, route }) => {
  const { eventId } = route.params;
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [checkInCount, setCheckInCount] = useState(0);

  useEffect(() => {
    fetchEventDetails();
  }, [eventId]);

  const fetchEventDetails = async () => {
    setLoading(true);
    try {
      const eventData = await getEventById(eventId);
      // Map to camelCase for UI compatibility
      const mapped = {
        ...eventData,
        eventDate: eventData.event_date,
        eventTime: eventData.event_time,
        eventType: eventData.event_type,
        isPaid: eventData.is_paid,
        rsvps: {
          items: (eventData.rsvps || []).map(r => ({
            ...r,
            createdAt: r.created_at,
            user: r.user ? {
              ...r.user,
              accountType: r.user.account_type,
            } : null,
          })),
        },
      };
      setEvent(mapped);
      setRsvps(mapped.rsvps?.items || []);
      const checkIns = await getEventCheckIns(eventId);
      setCheckInCount(checkIns?.length ?? 0);
    } catch (error) {
      console.error('Error fetching event:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEvent = async () => {
    const confirmDelete = Platform.OS === 'web'
      ? window.confirm('Are you sure you want to delete this event? This cannot be undone.')
      : await new Promise((resolve) => {
          Alert.alert(
            'Delete Event',
            'Are you sure you want to delete this event? This cannot be undone.',
            [
              { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
              { text: 'Delete', style: 'destructive', onPress: () => resolve(true) },
            ]
          );
        });

    if (!confirmDelete) return;

    try {
      await deleteEvent(eventId);

      navigation.goBack();
      setTimeout(() => {
        showToast('Event deleted successfully', 'success');
      }, 100);
    } catch (error) {
      console.error('Error deleting event:', error);
      showToast('Failed to delete event. Please try again.', 'error');
    }
  };

  const handleMessageAttendees = () => {
    showToast('Group messaging feature coming soon!', 'info');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const getSkillLevel = (rating) => {
    if (!rating) return 'Beginner';
    if (rating < 1200) return 'Beginner';
    if (rating < 1600) return 'Intermediate';
    return 'Advanced';
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <LoadingAnimation size={525} />
      </View>
    );
  }

  if (!event) {
    return (
      <View style={styles.errorContainer}>
        <Icon name="error-outline" size={64} color="#ccc" />
        <Text style={styles.errorText}>Event not found</Text>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Text style={styles.backButtonText}>Go Back</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButtonHeader}>
          <Icon name="arrow-back" size={24} color="#333" />
          <Text style={styles.backText}>Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Manage Event</Text>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Event Details Card */}
        <View style={styles.eventCard}>
          <View style={styles.eventHeader}>
            <Text style={styles.eventTitle}>{event.title}</Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <TouchableOpacity
                style={[styles.editButton, { backgroundColor: '#E8F5E9', borderColor: '#94EB98' }]}
                onPress={() => navigation.navigate('CreateEvent', {
                  prefillEvent: {
                    title: event.title,
                    eventType: event.eventType,
                    location: event.location,
                    description: event.description,
                    capacity: event.capacity,
                  },
                })}
              >
                <Text style={[styles.editButtonText, { color: '#333' }]}>Duplicate</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.editButton}
                onPress={() => navigation.navigate('CreateEvent', { 
                  mode: 'edit', 
                  eventData: event 
                })}
              >
                <Text style={styles.editButtonText}>Edit Details</Text>
              </TouchableOpacity>
            </View>
          </View>

          <View style={styles.eventDetail}>
            <Icon name="event" size={20} color="#666" />
            <Text style={styles.eventDetailText}>{formatDate(event.eventDate)}</Text>
          </View>

          <View style={styles.eventDetail}>
            <Icon name="schedule" size={20} color="#666" />
            <Text style={styles.eventDetailText}>{event.eventTime || 'Time TBD'}</Text>
          </View>

          <View style={styles.eventDetail}>
            <Icon name="location-on" size={20} color="#666" />
            <Text style={styles.eventDetailText}>{event.location || 'Location TBD'}</Text>
          </View>

          <View style={styles.eventDetail}>
            <Icon name="category" size={20} color="#666" />
            <View style={styles.eventTypeTag}>
              <Text style={styles.eventTypeText}>{event.eventType}</Text>
            </View>
          </View>

          {event.description && (
            <View style={styles.eventDescription}>
              <Text style={styles.descriptionLabel}>Description:</Text>
              <Text style={styles.descriptionText}>{event.description}</Text>
            </View>
          )}

          {event.capacity && (
            <View style={styles.capacityInfo}>
              <Text style={styles.capacityText}>
                Capacity: {rsvps.length} / {event.capacity}
              </Text>
            </View>
          )}
        </View>

        {/* Check-in Section */}
        <View style={styles.checkInSection}>
          <Text style={styles.sectionTitle}>Check-in</Text>
          <View style={styles.checkInModeRow}>
            {['OFF', 'QR_CODE', 'LOCATION'].map((mode) => (
              <TouchableOpacity
                key={mode}
                style={[
                  styles.checkInModeBtn,
                  (event.check_in_mode || 'OFF') === mode && styles.checkInModeBtnActive,
                ]}
                onPress={async () => {
                  try {
                    await updateEventCheckInMode(eventId, mode);
                    setEvent((prev) => (prev ? { ...prev, check_in_mode: mode } : null));
                  } catch (e) {
                    console.error('Update check-in mode:', e);
                  }
                }}
              >
                <Text
                  style={[
                    styles.checkInModeBtnText,
                    (event.check_in_mode || 'OFF') === mode && styles.checkInModeBtnTextActive,
                  ]}
                >
                  {mode === 'OFF' ? 'Off' : mode === 'QR_CODE' ? 'QR code' : 'Location'}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          <View style={styles.checkInStats}>
            <Text style={styles.checkInStatsText}>
              {checkInCount} checked in / {rsvps.length} RSVPs
            </Text>
          </View>
          {(event.check_in_mode === 'QR_CODE' || event.check_in_mode === 'LOCATION') && (
            <TouchableOpacity
              style={styles.openCheckInButton}
              onPress={() => navigation.navigate('EventCheckIn', { eventId })}
              activeOpacity={0.8}
            >
              <Icon name="qr-code" size={20} color="#fff" />
              <Text style={styles.openCheckInButtonText}>Open Check-In</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* RSVPs Section */}
        <View style={styles.rsvpSection}>
          <Text style={styles.sectionTitle}>RSVPs ({rsvps.length})</Text>

          {rsvps.length === 0 ? (
            <View style={styles.emptyState}>
              <Icon name="people-outline" size={48} color="#ccc" />
              <Text style={styles.emptyStateText}>No RSVPs yet</Text>
              <Text style={styles.emptyStateSubtext}>
                Players will appear here when they RSVP to your event
              </Text>
            </View>
          ) : (
            rsvps.map((rsvp) => (
              <TouchableOpacity
                key={rsvp.id}
                style={styles.rsvpItem}
                onPress={() => navigation.navigate('UserDetail', { userId: rsvp.user.id })}
                activeOpacity={0.7}
              >
                <View style={styles.rsvpCheck}>
                  <Icon name="check-circle" size={20} color="#94EB98" />
                </View>
                <View style={styles.rsvpInfo}>
                  <Text style={styles.rsvpName}>{rsvp.user.username}</Text>
                  <Text style={styles.rsvpSkill}>
                    {rsvp.user.rating ? `${getSkillLevel(rsvp.user.rating)}` : 'Unrated'}
                  </Text>
                  <Text style={styles.rsvpTimestamp}>
                    Registered {formatTimestamp(rsvp.createdAt)}
                  </Text>
                </View>
                <Icon name="chevron-right" size={20} color="#999" />
              </TouchableOpacity>
            ))
          )}

          {rsvps.length > 0 && (
            <TouchableOpacity
              style={styles.messageButton}
              onPress={handleMessageAttendees}
              activeOpacity={0.8}
            >
              <Icon name="message" size={20} color="#333" />
              <Text style={styles.messageButtonText}>Message Attendees</Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Delete Event Button */}
        <TouchableOpacity
          style={styles.deleteButton}
          onPress={handleDeleteEvent}
          activeOpacity={0.8}
        >
          <Icon name="delete" size={20} color="#FF4444" />
          <Text style={styles.deleteButtonText}>Delete Event</Text>
        </TouchableOpacity>

        <View style={{ height: 100 }} />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
    paddingTop: Platform.OS === 'ios' ? 50 : 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
    padding: 40,
  },
  errorText: {
    fontSize: 20,
    fontWeight: '600',
    color: '#999',
    marginTop: 16,
    marginBottom: 24,
  },
  header: {
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButtonHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  backText: {
    fontSize: 16,
    color: '#333',
    marginLeft: 4,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#333',
  },
  content: {
    flex: 1,
  },
  eventCard: {
    margin: 16,
    padding: 20,
    backgroundColor: '#f8f8f8',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  eventHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  eventTitle: {
    flex: 1,
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginRight: 12,
  },
  editButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#94EB98',
  },
  editButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  eventDetail: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  eventDetailText: {
    fontSize: 16,
    color: '#666',
    marginLeft: 12,
  },
  eventTypeTag: {
    backgroundColor: '#94EB98',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
    marginLeft: 12,
  },
  eventTypeText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
  },
  eventDescription: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  descriptionLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
    marginBottom: 8,
  },
  descriptionText: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
  capacityInfo: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  capacityText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
  },
  rsvpSection: {
    margin: 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  checkInSection: {
    margin: 16,
    padding: 16,
    backgroundColor: '#f8f8f8',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  checkInModeRow: {
    flexDirection: 'row',
    marginBottom: 12,
    gap: 8,
  },
  checkInModeBtn: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ddd',
    alignItems: 'center',
  },
  checkInModeBtnActive: {
    backgroundColor: '#94EB98',
    borderColor: '#94EB98',
  },
  checkInModeBtnText: {
    fontSize: 14,
    color: '#666',
  },
  checkInModeBtnTextActive: {
    color: '#333',
    fontWeight: '600',
  },
  checkInStats: {
    marginBottom: 12,
  },
  checkInStatsText: {
    fontSize: 15,
    color: '#666',
  },
  openCheckInButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#333',
    borderRadius: 8,
    gap: 8,
  },
  openCheckInButtonText: {
    fontSize: 16,
    color: '#fff',
    fontWeight: '600',
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyStateText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#999',
    marginTop: 12,
    marginBottom: 4,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#bbb',
    textAlign: 'center',
    paddingHorizontal: 40,
  },
  rsvpItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#f8f8f8',
    borderRadius: 8,
    marginBottom: 8,
  },
  rsvpCheck: {
    marginRight: 12,
  },
  rsvpInfo: {
    flex: 1,
  },
  rsvpName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 2,
  },
  rsvpSkill: {
    fontSize: 14,
    color: '#666',
    marginBottom: 2,
  },
  rsvpTimestamp: {
    fontSize: 12,
    color: '#999',
  },
  messageButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 16,
    backgroundColor: '#94EB98',
    borderRadius: 12,
    marginTop: 16,
  },
  messageButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
  },
  deleteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    margin: 16,
    padding: 16,
    backgroundColor: '#fff',
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#FF4444',
  },
  deleteButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FF4444',
  },
  backButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: '#94EB98',
    borderRadius: 8,
  },
  backButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
  },
});

export default ManageEventScreen;

